import React, { useState, useRef, useEffect } from 'react';
import { FiX, FiPlus, FiUser, FiCheck } from 'react-icons/fi';
import { useAuth } from '../../../hooks/useAuth';
import { useTask } from '../contexts/TaskContext';
import toast from 'react-hot-toast';

const CollaboratorSelector = ({ task, onChange, compact = true }) => {
  const { user } = useAuth();
  const { users } = useTask();
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAssign = (email) => {
    // Toggle assignment
    const currentAssignments = task.asignados || [];
    let newAssignments;

    if (currentAssignments.includes(email)) {
      newAssignments = []; // For simplicity, single assignee for now or remove if clicked
      // If we want multiple: newAssignments = currentAssignments.filter(e => e !== email);
    } else {
      newAssignments = [email]; // Replace with new assignee (Single Assignee Mode)
      // If we want multiple: newAssignments = [...currentAssignments, email];
    }

    onChange(newAssignments);
    setIsOpen(false);
  };

  const handleRemove = () => {
    onChange([]);
    setIsOpen(false);
  };

  // Get current assignee data
  const assignedEmail = task.asignados?.[0];
  const assignedUser = users.find(u => u.email === assignedEmail);

  // Resolve display data
  const isCurrentUser = user && user.email === assignedEmail;
  const displayName = isCurrentUser ? (user.displayName || assignedUser?.displayName || assignedEmail) : (assignedUser?.displayName || assignedEmail);
  const photoURL = isCurrentUser ? (user.photoURL || assignedUser?.photoURL) : assignedUser?.photoURL;

  return (
    <div className="relative inline-block" ref={wrapperRef}>
      {/* Trigger: Avatar or Empty State Circle */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="cursor-pointer group relative"
        title={displayName || "Asignar responsable"}
      >
        {assignedEmail ? (
          <div className="w-8 h-8 rounded-full ring-2 ring-white hover:ring-blue-100 transition-all shadow-sm">
            {photoURL ? (
              <img src={photoURL} alt={displayName} className="w-full h-full rounded-full object-cover" />
            ) : (
              <div className="w-full h-full rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold border border-purple-200">
                {displayName?.charAt(0).toUpperCase()}
              </div>
            )}

            {/* Remove button on hover (Desktop) */}
            <div
              className="absolute -top-1 -right-1 bg-white rounded-full p-0.5 shadow-sm border border-gray-100 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer text-gray-400 hover:text-red-500"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
            >
              <FiX size={10} />
            </div>
          </div>
        ) : (
          <div className="w-8 h-8 rounded-full border border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:text-blue-500 hover:border-blue-400 hover:bg-blue-50 transition-all bg-white">
            <FiUser size={14} />
          </div>
        )}
      </div>

      {/* Popover */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top-left">
          <div className="p-3 border-b border-gray-50 bg-gray-50/50">
            <input
              type="text"
              placeholder="Buscar persona..."
              className="w-full text-xs px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          </div>

          <div className="max-h-60 overflow-y-auto py-1">
            <div className="px-3 py-1 text-[10px] uppercase font-bold text-gray-400 tracking-wider">Sugeridos</div>
            {users.map(u => {
              const isSelected = task.asignados?.includes(u.email);
              const isMe = user.email === u.email;
              const uName = isMe ? (user.displayName || u.displayName) : u.displayName;
              const uPhoto = isMe ? (user.photoURL || u.photoURL) : u.photoURL;

              return (
                <button
                  key={u.id}
                  onClick={() => handleAssign(u.email)}
                  className={`w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50/50' : ''}`}
                >
                  <div className="relative">
                    {uPhoto ? (
                      <img src={uPhoto} alt="" className="w-8 h-8 rounded-full object-cover border border-gray-100" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center text-xs font-bold border border-indigo-100">
                        {uName?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    {isSelected && (
                      <div className="absolute -bottom-1 -right-1 bg-blue-500 text-white rounded-full p-0.5 border-2 border-white">
                        <FiCheck size={10} />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm truncate ${isSelected ? 'font-semibold text-blue-700' : 'font-medium text-gray-700'}`}>
                      {uName} {isMe && <span className="text-gray-400 font-normal">(Tú)</span>}
                    </div>
                    <div className="text-xs text-gray-400 truncate">{u.email}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {assignedEmail && (
            <div className="p-2 border-t border-gray-50 bg-gray-50/30">
              <button
                onClick={handleRemove}
                className="w-full py-1.5 text-xs text-red-600 hover:bg-red-50 rounded px-2 flex items-center justify-center gap-2 transition-colors"
              >
                <FiX size={12} /> Desasignar tarea
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CollaboratorSelector;
